import * as React from 'react';

import type { W_FieldReport_FindAllReportTypesForCompany_VM } from 'ab-api/web/fieldReport/findAllReportTypesForCompany';

import { filterMap } from '@acceligentllc/shared/utils/array';

import { Button, TextInput, SearchInput } from '@acceligentllc/storybook';

import Checkbox from 'af-components/Controls/Checkbox';
import CustomModal from 'af-components/CustomModal';
import Tooltip from 'af-components/Tooltip';

import FieldsPane from './FieldsPane';
import type { SelectedFieldRef } from './ReportTypeCards';
import ReportTypeCards from './ReportTypeCards';
import styles from './styles.module.scss';
import ReportTypeRevisionsModal from '../ReportTypeRevisionsModal';
import type { ColumnDataSourceFM, TemplateColumnFM } from '../Shared/formModel';
import { buildFieldLabel, findMatchingFieldInRevision, getBlockById, getLatestReportTypes, indexReportTypes, withCidDS } from '../Shared/helpers';

interface OwnProps {
	closeModal: () => void;
	showModal: boolean;
	save: (column: TemplateColumnFM) => void;
	initialState: Nullable<TemplateColumnFM>;
	companyFieldReportTypes: Nullable<W_FieldReport_FindAllReportTypesForCompany_VM>;
	isEdit: boolean;
	baselineColumn: Nullable<TemplateColumnFM>;
}

export enum ReportTypeDoNotUseMode {
	KEEP = 'KEEP', // Keep previous revision data
	CLEAR = 'CLEAR', // Clear all previous data
	NONE = 'none', // Do not use not selected
}

const TOOLTIP_MESSAGE = <div className={styles.tooltip}>
	<b>Mark others as Do Not Use</b>
	Applies "Do not use" to all unselected blocks. Selected blocks will not be affected.
</div>;

type Props = OwnProps;

const AddFieldsToColumnsModal: React.FC<Props> = (props: Props) => {

	const {
		closeModal,
		showModal,
		save,
		initialState,
		companyFieldReportTypes,
		isEdit,
		baselineColumn,
	} = props;

	const [column, setColumn] = React.useState<Nullable<TemplateColumnFM>>(initialState);
	const [search, setSearch] = React.useState<string>('');
	const [showRevisions, setShowRevisions] = React.useState(false);
	const [revisionsLatestRtId, setRevisionsLatestRtId] = React.useState<Nullable<number>>(null);

	const openRevisionsFor = React.useCallback((latestRtId: number) => {
		setRevisionsLatestRtId(latestRtId);
		setShowRevisions(true);
	}, []);

	const closeRevisions = React.useCallback(() => {
		setShowRevisions(false);
		setRevisionsLatestRtId(null);
	}, []);

	const latestReportTypes = React.useMemo(() => getLatestReportTypes(companyFieldReportTypes),
		[companyFieldReportTypes]
	);

	React.useEffect(() => {
		if (!initialState || !showModal) {
			setColumn(null);
		} else {
			setColumn({
				...initialState,
				dataSources: (initialState.dataSources ?? []).map((d) =>
					d._cid ? d : withCidDS(d)
				),
			});
		}
	}, [initialState, showModal]);

	// apply the patch from revisions modal only to the family of revisionsLatestRtId

	const applyRevisionsPatch = React.useCallback(
		(patch: { addSelections: ColumnDataSourceFM[]; ignoreRevisionIds: number[]; }) => {
			if (!companyFieldReportTypes || !revisionsLatestRtId) {
				return;
			}

			const { byId: reportTypesById, byFamily: reportTypesByFamily } = indexReportTypes(companyFieldReportTypes);
			const latestRt = reportTypesById.get(revisionsLatestRtId);
			if (!latestRt) {
				return;
			}

			// familyKey must match what BE sends and what indexReportTypes uses
			const familyKey = latestRt.familyKey;
			const chain = reportTypesByFamily.get(familyKey) ?? [];
			const familyRtIds = chain.map((c) => c.id);

			setColumn((prev) => {
				if (!prev) {
					return prev;
				}

				const prevDataSources = prev.dataSources;

				// existing data sources per revision in this family (by reportTypeId)
				const existingByRtId = prevDataSources.reduce<Map<number, ColumnDataSourceFM>>(
					(acc, dataSource) => {
						const rtId = dataSource.field?.reportTypeId;

						if (
							rtId &&
							familyRtIds.includes(rtId) &&
							!acc.has(rtId)
						) {
							acc.set(rtId, dataSource);
						}

						return acc;
					},
					new Map()
				);

				// keep data sources outside this family
				const outsideDataSources = prevDataSources.filter((ds) => {
					const rtId = ds.field?.reportTypeId;
					return !rtId || !familyRtIds.includes(rtId);
				});

				// merge new selections with existing DS (preserve id/_cid if we had a data source for that revision)
				const mergedFamily: ColumnDataSourceFM[] = [];
				for (const sel of patch.addSelections) {
					if (!sel.field) {
						continue; // should not happen
					}
					const rtId = sel.field.reportTypeId;
					const existing = existingByRtId.get(rtId);

					if (existing) {
						mergedFamily.push({
							...existing,
							field: {
								reportTypeId: sel.field.reportTypeId,
								reportBlockId: sel.field.reportBlockId,
								reportBlockFieldId: sel.field.reportBlockFieldId,
							},
						});
					} else {
						// ensure we always have _cid on brand-new DS
						mergedFamily.push(sel._cid ? sel : withCidDS(sel));
					}
				}

				// ---- exclusions handling ----
				// start from current exclusions
				let excludedNew = [...prev.excludedFieldReportIds];

				// add newly ignored revisions
				for (const id of patch.ignoreRevisionIds) {
					if (!excludedNew.includes(id)) {
						excludedNew.push(id);
					}
				}

				// any revision we now actively map must NOT stay excluded
				for (const sel of patch.addSelections) {
					if (sel.field) {
						excludedNew = excludedNew.filter((id) => id !== sel.field.reportTypeId);
					}
				}

				return {
					...prev,
					dataSources: outsideDataSources.concat(mergedFamily),
					excludedFieldReportIds: excludedNew,
				};
			});

			setShowRevisions(false);
			setRevisionsLatestRtId(null);
		},
		[companyFieldReportTypes, revisionsLatestRtId]
	);

	const onSave = React.useCallback(() => {
		if (!column) {
			return;
		}
		save(column);
		closeModal();
	}, [column, save, closeModal]);

	const setColumnName = React.useCallback((name: string) => {
		setColumn((prev) => {
			if (!prev) {
				return prev;
			}
			return { ...prev, name };
		});
	}, []);

	const { selectedColumns, outdatedFields, outdatedLatestIds } = React.useMemo(() => {
		const dataSources = (column?.dataSources ?? []).filter((d) => !!d.field);
		const _resultSelected: {
			latestRtId: number;
			label: string;
		}[] = [];
		const _resultOutdated: string[] = [];
		const _outdatedLatestIds: number[] = [];

		if (!companyFieldReportTypes?.reportTypes?.length) {
			return {
				selectedColumns: _resultSelected,
				outdatedFields: _resultOutdated,
				outdatedLatestIds: _outdatedLatestIds,
			};
		}

		const excludedFieldReportIds = column?.excludedFieldReportIds ?? [];

		// all non-excluded revisions per report type revision chain
		const allRevisionsByFamily = new Map<string, number[]>(); // <familyKey, revisionIds>
		const latestIdByFamily = new Map<string, number>(); // <familyKey, latestRevisionId>
		companyFieldReportTypes.reportTypes.forEach((reportType) => {
			if (excludedFieldReportIds.includes(reportType.id)) {
				return;
			}
			const key = reportType.familyKey;
			const revisionIds = allRevisionsByFamily.get(key) ?? [];
			revisionIds.push(reportType.revision);
			allRevisionsByFamily.set(key, revisionIds);

			const latestRevisionId = latestIdByFamily.get(key);
			if (!latestRevisionId) {
				latestIdByFamily.set(key, reportType.id);
			} else {
				const prevRt = (companyFieldReportTypes.reportTypes ?? []).find((_reportType) => _reportType.id === latestRevisionId)!;
				if (reportType.revision > prevRt.revision) {
					latestIdByFamily.set(key, reportType.id);
				}
			}
		});

		// figure out which report type revision chains are in "Do Not Use"
		const { byFamily: reportTypesByFamily } = indexReportTypes(companyFieldReportTypes);
		const doNotUseFamiliesUniqueIds = Array.from(reportTypesByFamily.entries()).reduce<string[]>(
			(acc, [familyKey, revisions]) => {
				if (!revisions.length) {
					return acc;
				}

				// reportTypesByFamily is sorted DESC, so first is latest
				const latestRevisionId = revisions[0].id;

				const isLatestExcluded = excludedFieldReportIds.includes(latestRevisionId);
				const allExcluded = revisions.every((r) =>
					excludedFieldReportIds.includes(r.id)
				);

				if (isLatestExcluded || allExcluded) {
					acc.push(familyKey);
				}

				return acc;
			},
			[]
		);

		// selected revisions per report type revision chain + first label per family
		const selectedRevisionsByFamily = new Map<string, number[]>(); // <familyKey, revisionIds>
		const firstDataSourceByFamily = new Map<string, ColumnDataSourceFM>(); // <familyKey, firstDataSource>

		// making sure to sort so we get correct names
		dataSources.sort((a, b) => {
			const ax = a.id ?? null;
			const bx = b.id ?? null;

			if (ax === null && bx === null) {
				return 0;
			}
			if (ax === null) {
				return -1;
			}
			if (bx === null) {
				return 1;
			}

			return ax - bx;
		}).forEach((dataSource) => {
			if (!dataSource.field) {
				return;
			}
			const rtRow = companyFieldReportTypes.reportTypes.find((rt) => rt.id === dataSource.field!.reportTypeId);
			if (!rtRow) {
				return;
			}

			const key = rtRow.familyKey;

			if (!firstDataSourceByFamily.has(key)) {
				firstDataSourceByFamily.set(key, dataSource);
			}

			const selectedRevisionIds = selectedRevisionsByFamily.get(key) ?? [];
			selectedRevisionIds.push(rtRow.revision);
			selectedRevisionsByFamily.set(key, selectedRevisionIds);
		});

		// Decide per report type revision chain if it is outdated
		for (const [familyKey, firstDataSource] of firstDataSourceByFamily.entries()) {
			// Skip chains that are tagged as "Do Not Use"
			if (doNotUseFamiliesUniqueIds.includes(familyKey)) {
				continue;
			}

			const label = buildFieldLabel(companyFieldReportTypes, firstDataSource);
			if (!label) {
				continue; // should never happen
			}

			const latestId = latestIdByFamily.get(familyKey);
			if (!latestId) {
				continue; // should never happen
			}

			const selectedRevisionsIds = selectedRevisionsByFamily.get(familyKey) ?? [];
			const allFamilyRevisionIds = allRevisionsByFamily.get(familyKey) ?? [];
			const isOutdated = selectedRevisionsIds.length > 0 && selectedRevisionsIds.length < allFamilyRevisionIds.length;

			if (isOutdated) {
				_resultOutdated.push(label);
				_outdatedLatestIds.push(latestId);
			} else {
				_resultSelected.push({ latestRtId: latestId, label });
			}
		}

		_resultSelected.sort((a, b) => a.label.localeCompare(b.label));
		_resultOutdated.sort((a, b) => a.localeCompare(b));

		return {
			selectedColumns: _resultSelected,
			outdatedFields: _resultOutdated,
			outdatedLatestIds: _outdatedLatestIds,
		};
	}, [companyFieldReportTypes, column?.dataSources, column?.excludedFieldReportIds]);

	const selectedFields: SelectedFieldRef[] = React.useMemo(() => {
		return filterMap(
			column?.dataSources ?? [],
			(ds) => !!ds.field,
			(ds) => ({
				reportTypeId: ds.field!.reportTypeId,
				reportBlockId: ds.field!.reportBlockId,
				reportBlockFieldId: ds.field!.reportBlockFieldId,
			})
		);
	}, [column]);

	const handleSelectField = React.useCallback(
		(latestRtId: number, latestRbId: number, latestRbfId: number) => {
			setColumn((prev) => {
				if (!prev || !companyFieldReportTypes) {
					return prev;
				}

				const { byId: reportTypesById, byFamily: reportTypesByFamily } =
					indexReportTypes(companyFieldReportTypes);

				const latestRt = reportTypesById.get(latestRtId);
				if (!latestRt) {
					return prev;
				}

				const latestBlock = getBlockById(latestRt, latestRbId);
				const latestField = latestBlock?.fields.find(
					(f) => f.id === latestRbfId
				);
				if (!latestBlock || !latestField) {
					return prev;
				}

				const familyKey = latestRt.familyKey;
				const familyChain = reportTypesByFamily.get(familyKey) ?? [];
				const familyRtIds = familyChain.map((c) => c.id);

				const prevDataSources = prev.dataSources ?? [];
				const excluded = prev.excludedFieldReportIds ?? [];

				// Split DS into "this family" vs "outside"
				const splitColumnsMap = prevDataSources.reduce(
					(acc, ds) => {
						const rtId = ds.field?.reportTypeId;
						if (rtId && familyRtIds.indexOf(rtId) !== -1) {
							acc.family.push(ds);
						} else {
							acc.outside.push(ds);
						}
						return acc;
					},
					{
						family: [] as ColumnDataSourceFM[],
						outside: [] as ColumnDataSourceFM[],
					}
				);

				const isOutdatedFamily =
					outdatedLatestIds.indexOf(latestRtId) !== -1;

				// Map family DS by rtId so we can reuse id/_cid
				const familyByRtId = splitColumnsMap.family.reduce<{ [rtId: number]: ColumnDataSourceFM; }>(
					(acc, ds) => {
						const rtId = ds.field?.reportTypeId;
						if (rtId && !acc[rtId]) {
							acc[rtId] = ds;
						}
						return acc;
					},
					{}
				);

				// Always patch latest revision
				const prevLatest = familyByRtId[latestRtId];
				const patchedLatest: ColumnDataSourceFM = prevLatest
					? {
						...prevLatest,
						field: {
							...(prevLatest.field ?? {}),
							reportTypeId: latestRtId,
							reportBlockId: latestRbId,
							reportBlockFieldId: latestRbfId,
						},
					}
					: withCidDS({
						field: {
							reportTypeId: latestRtId,
							reportBlockId: latestRbId,
							reportBlockFieldId: latestRbfId,
						},
					});

				familyByRtId[latestRtId] = patchedLatest;

				if (isOutdatedFamily) {
					// --------  OUTDATED FAMILY --------
					// Treat as PATCH:
					// - keep existing DS in this family
					// - override latest with new field
					// - auto-add only missing compatible revisions
					// - DO NOT drop existing "Do not use" markers for unrelated revisions

					for (let i = 0; i < familyChain.length; i++) {
						const rt = familyChain[i];

						if (rt.id === latestRtId) {
							continue;
						}

						// Already has a DS for this revision -> leave as-is
						if (familyByRtId[rt.id]) {
							continue;
						}

						// Explicitly "Do not use" this revision -> respect that
						if (excluded.indexOf(rt.id) !== -1) {
							continue;
						}

						const match = findMatchingFieldInRevision(
							rt,
							latestBlock.uniqueId,
							latestField.name,
							latestField.unit
						);
						if (!match) {
							continue;
						}

						familyByRtId[rt.id] = withCidDS({
							field: {
								reportTypeId: rt.id,
								reportBlockId: match.reportBlockId,
								reportBlockFieldId: match.reportBlockFieldId,
							},
						});
					}

					// Build mergedFamily preserving previous order as much as we can:
					//  1) start from previous family DS order (overwritten by new versions where needed)
					//  2) append any new revisions that didn't exist before
					const nextFamily = splitColumnsMap.family.reduce(
						(acc, ds) => {
							const rtId = ds.field?.reportTypeId;
							if (!rtId) {
								return acc;
							}
							const current = familyByRtId[rtId];
							if (current) {
								acc.push(current);
								delete familyByRtId[rtId];
							}
							return acc;
						},
						[] as ColumnDataSourceFM[]
					);

					const remaining = Object.keys(familyByRtId).reduce(
						(innerAcc, key) => {
							const rtId = +key;
							const ds = familyByRtId[rtId];
							if (ds) {
								innerAcc.push(ds);
							}
							return innerAcc;
						},
						[] as ColumnDataSourceFM[]
					);

					const mergedFamily = nextFamily.concat(remaining);

					// Now exclusions: only clear exclusion for revisions which we actively map
					const activeFamilyRtIds = mergedFamily.reduce(
						(acc, ds) => {
							const rtId = ds.field?.reportTypeId;
							if (
								rtId &&
								acc.indexOf(rtId) === -1
							) {
								acc.push(rtId);
							}
							return acc;
						},
						[] as number[]
					);

					const nextExcluded = excluded.filter(
						(id) => activeFamilyRtIds.indexOf(id) === -1
					);

					return {
						...prev,
						dataSources: splitColumnsMap.outside.concat(mergedFamily),
						excludedFieldReportIds: nextExcluded,
					};
				} else {

					// -------- FAMILY NOT OUTDATED --------
					// Rebuild chain based on this field, but:
					// - keep previous DS where there is no matching field
					// - only clear exclusions for revisions that we actively map

					const nextFamily = [] as ColumnDataSourceFM[];

					// latest first
					nextFamily.push(patchedLatest);

					for (let i = 0; i < familyChain.length; i++) {
						const revision = familyChain[i];

						if (revision.id === latestRtId) {
							continue;
						}

						const match = findMatchingFieldInRevision(
							revision,
							latestBlock.uniqueId,
							latestField.name,
							latestField.unit
						);

						const prevDs = familyByRtId[revision.id];

						if (!match) {
							// no compatible field in this revision -> keep old DS if it existed
							if (prevDs) {
								nextFamily.push(prevDs);
							}
							continue;
						}

						const rebuilt: ColumnDataSourceFM = prevDs
							? {
								...prevDs,
								field: {
									...(prevDs.field ?? {}),
									reportTypeId: revision.id,
									reportBlockId: match.reportBlockId,
									reportBlockFieldId: match.reportBlockFieldId,
								},
							}
							: withCidDS({
								field: {
									reportTypeId: revision.id,
									reportBlockId: match.reportBlockId,
									reportBlockFieldId: match.reportBlockFieldId,
								},
							});

						nextFamily.push(rebuilt);
					}

					const activeFamilyRtIds = nextFamily.reduce<number[]>(
						(acc, ds) => {
							const rtId = ds.field.reportTypeId;
							if (acc.indexOf(rtId) === -1) {
								acc.push(rtId);
							}
							return acc;
						},
						[]
					);

					const nextExcluded = excluded.filter(
						(id) => activeFamilyRtIds.indexOf(id) === -1
					);

					return {
						...prev,
						dataSources: splitColumnsMap.outside.concat(nextFamily),
						excludedFieldReportIds: nextExcluded,
					};
				}
			});
		},
		[companyFieldReportTypes, outdatedLatestIds]
	);

	const handleMarkDoNotUse = React.useCallback(
		(rtId: number, keepPreviousDataSources: boolean = true) => {
			setColumn((prev) => {
				if (!prev) {
					return prev;
				}

				const { byId: reportTypesById, byFamily: reportTypesByFamily } = indexReportTypes(companyFieldReportTypes);
				const latestRt = reportTypesById.get(rtId);
				if (!latestRt) {
					return prev;
				}

				const familyKey = latestRt.familyKey;
				const chain = reportTypesByFamily.get(familyKey) ?? [];
				const familyRtIds = chain.map((c) => c.id);

				// start from current data source, drop anything for this family
				let nextDS = (prev.dataSources ?? []).filter((ds) => {
					const id = ds.field?.reportTypeId;
					return !id || !familyRtIds.includes(id);
				});

				// in edit mode we can keep previous data sources - in that case keep everything as it was before editing, but without latest revision
				if (isEdit && keepPreviousDataSources && baselineColumn) {
					const baseForFamily = filterMap(
						baselineColumn.dataSources,
						(ds) => !!ds.field && familyRtIds.includes(ds.field!.reportTypeId) && ds.field!.reportTypeId !== rtId,
						(d) => (d._cid ? d : withCidDS(d))
					);
					if (baseForFamily.length) {
						nextDS = nextDS.concat(baseForFamily);
					}
				}
				// in create mode (or edit + clear): nothing is restored -> remains cleared.

				// Normalize excluded: clear all family ids first, then apply mode
				const nextExcluded = prev.excludedFieldReportIds.filter((id) => !familyRtIds.includes(id));

				if (keepPreviousDataSources) {
					// Exclude only the latest revision so we stop from now on
					nextExcluded.push(rtId);
				} else {
					// Clear: exclude all revisions of this family
					familyRtIds.forEach((id) => nextExcluded.push(id));
				}

				return {
					...prev,
					dataSources: nextDS,
					excludedFieldReportIds: nextExcluded,
				};
			});
		},
		[companyFieldReportTypes, isEdit, baselineColumn, setColumn]
	);

	const familyStateByKey = React.useMemo(() => {
		if (!companyFieldReportTypes) {
			return new Map<
				string,
				{
					name: string;
					latestRtId: number;
					hasLatestSelected: boolean;
					hasOlderSelected: boolean;
					hasAnySelected: boolean;
					isLatestExcluded: boolean;
					areAllExcluded: boolean;
				}
			>();
		}

		const { byFamily: reportTypesByFamily } = indexReportTypes(companyFieldReportTypes);
		const excluded = column?.excludedFieldReportIds ?? [];

		const selectedRtIds = (column?.dataSources ?? []).reduce<number[]>((acc, ds) => {
			const rtId = ds.field?.reportTypeId;

			if (rtId && !excluded.includes(rtId) && !acc.includes(rtId)) {
				acc.push(rtId);
			}

			return acc;
		}, []);

		return Array.from(reportTypesByFamily.entries()).reduce((acc, [familyKey, revisions]) => {
			if (!revisions.length) {
				return acc;
			}

			const latest = revisions[0];
			const hasLatestSelected = selectedRtIds.includes(latest.id);
			const hasOlderSelected = revisions.some((revision, index) => {
				if (index === 0) {
					return false;
				}

				return selectedRtIds.includes(revision.id);
			});

			acc.set(familyKey, {
				name: latest.name,
				latestRtId: latest.id,
				hasLatestSelected,
				hasOlderSelected,
				hasAnySelected: hasLatestSelected || hasOlderSelected,
				isLatestExcluded: excluded.includes(latest.id),
				areAllExcluded: revisions.every((revision) => excluded.includes(revision.id)),
			});

			return acc;
		}, new Map());
	}, [companyFieldReportTypes, column?.dataSources, column?.excludedFieldReportIds]);

	const doNotUseReportTypes = React.useMemo(() => {
		return Array.from(familyStateByKey.values())
			.filter((familyState) => {
				return !familyState.hasAnySelected
					&& (familyState.isLatestExcluded || familyState.areAllExcluded);
			})
			.map((familyState) => familyState.name)
			.sort((a, b) => a.localeCompare(b));
	}, [familyStateByKey]);

	const previouslySelectedReportTypes = React.useMemo(() => {
		return Array.from(familyStateByKey.values())
			.filter((familyState) => {
				return !familyState.hasLatestSelected && familyState.hasOlderSelected;
			})
			.map((familyState) => familyState.name)
			.sort((a, b) => a.localeCompare(b));
	}, [familyStateByKey]);

	const unAssociatedReportTypes = React.useMemo(() => {
		return Array.from(familyStateByKey.values())
			.filter((familyState) => {
				return !familyState.hasAnySelected
					&& !familyState.isLatestExcluded
					&& !familyState.areAllExcluded;
			})
			.map((familyState) => familyState.name)
			.sort((a, b) => a.localeCompare(b));
	}, [familyStateByKey]);

	const exclusionModeByLatestId = React.useMemo(() => {
		const map = new Map<number, ReportTypeDoNotUseMode>();
		const excluded = column?.excludedFieldReportIds ?? [];
		const { byFamily: reportTypesByFamily } = indexReportTypes(companyFieldReportTypes);

		latestReportTypes.forEach((reportType) => {
			const familyKey = reportType.familyKey;
			const revisionChain = reportTypesByFamily.get(familyKey) ?? [];

			if (!excluded.includes(reportType.id)) {
				map.set(reportType.id, ReportTypeDoNotUseMode.NONE);
				return;
			}
			const allExcluded = revisionChain.length > 0 && revisionChain.every((c) => excluded.includes(c.id));
			map.set(reportType.id, allExcluded ? ReportTypeDoNotUseMode.CLEAR : ReportTypeDoNotUseMode.KEEP);
		});

		return map;
	}, [column?.excludedFieldReportIds, companyFieldReportTypes, latestReportTypes]);

	const hasAssociatedOldRevisionByLatestId = React.useMemo(() => {
		const map = new Map<number, boolean>();

		if (!companyFieldReportTypes) {
			return map;
		}

		const { byFamily: reportTypesByFamily } = indexReportTypes(companyFieldReportTypes);

		latestReportTypes.forEach((latestReportType) => {
			const familyKey = latestReportType.familyKey;
			const revisionChain = reportTypesByFamily.get(familyKey) ?? [];

			const hasAssociatedOldRevision = (column?.dataSources ?? []).some((dataSource) => {
				const rtId = dataSource.field?.reportTypeId;

				if (!rtId || rtId === latestReportType.id) {
					return false;
				}

				return revisionChain.some((revision) => revision.id === rtId);
			});

			map.set(latestReportType.id, hasAssociatedOldRevision);
		});

		return map;
	}, [companyFieldReportTypes, latestReportTypes, column?.dataSources]);

	const setDefinition = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		if (!column) {
			return;
		}
		setColumn({ ...column, isDefinition: event.target.checked });
	}, [column]);

	const isSaveDisabled = React.useMemo(() =>
		outdatedFields.length > 0
		|| unAssociatedReportTypes.length > 0
		|| (selectedColumns.length === 0 && previouslySelectedReportTypes.length === 0),
		[outdatedFields.length, selectedColumns.length, unAssociatedReportTypes.length, previouslySelectedReportTypes.length]);

	const handleDisable = React.useCallback((latestRtId: number) => handleMarkDoNotUse(latestRtId, false)
		, [handleMarkDoNotUse]);

	const { modalTitle, enableBulkActions } = React.useMemo(() => {
		const initialStateHasNoDataSources = initialState?.dataSources.length === 0;

		if (initialStateHasNoDataSources) {
			return { modalTitle: 'Add Fields to Column', enableBulkActions: true };
		} else {
			return { modalTitle: 'Edit Fields in Column', enableBulkActions: false };
		}
	}, [initialState?.dataSources.length]);

	const handleMarkOthersAsDoNotUse = React.useCallback(() => {
		setColumn((prev) => {
			if (!prev || !companyFieldReportTypes) {
				return prev;
			}

			const { byFamily: reportTypesByFamily } = indexReportTypes(companyFieldReportTypes);
			const nextExcluded = [...prev.excludedFieldReportIds];

			Array.from(familyStateByKey.entries()).forEach(([familyKey, familyState]) => {
				const isUnassociated =
					!familyState.hasAnySelected
					&& !familyState.isLatestExcluded
					&& !familyState.areAllExcluded;

				if (!isUnassociated) {
					return;
				}

				const revisionChain = reportTypesByFamily.get(familyKey) ?? [];

				revisionChain.forEach((revision) => {
					if (!nextExcluded.includes(revision.id)) {
						nextExcluded.push(revision.id);
					}
				});
			});

			return {
				...prev,
				excludedFieldReportIds: nextExcluded,
			};
		});
	}, [companyFieldReportTypes, familyStateByKey]);

	const handleClearAll = React.useCallback(() => {
		setColumn((prev) => {
			if (!prev) {
				return prev;
			}

			return {
				...prev,
				dataSources: [],
				excludedFieldReportIds: [],
			};
		});
	}, []);

	const isMarkOthersAsDoNotUseDisabled = React.useMemo(() => {
		return unAssociatedReportTypes.length === 0;
	}, [unAssociatedReportTypes.length]);

	const isClearAllDisabled = React.useMemo(() => {
		return selectedColumns.length === 0
			&& previouslySelectedReportTypes.length === 0
			&& doNotUseReportTypes.length === 0
			&& outdatedFields.length === 0;
	}, [
		selectedColumns.length,
		previouslySelectedReportTypes.length,
		doNotUseReportTypes.length,
		outdatedFields.length,
	]);

	if (!column) {
		return null;
	}

	return (
		<>
			<CustomModal
				closeModal={closeModal}
				dialogClassName={styles.dialog}
				modalStyle="info"
				showModal={showModal}
				size="xxxl"
			>
				<CustomModal.Header
					closeModal={closeModal}
					title={modalTitle}
				/>
				<CustomModal.Body padding="none">
					<div className={styles['body-grid']}>
						<div className={styles.section}>
							<div className={styles['name-input']}>
								<TextInput
									isRequired={true}
									label="Column Name"
									onChange={setColumnName}
									placeholder="Column Name"
									value={column.name}
								/>
							</div>

							<div className={styles.checkbox}>
								<Checkbox
									handleChange={setDefinition}
									isChecked={column.isDefinition}
								/>
								<span>Use as Definition Column</span>
							</div>
						</div>

						<div className={styles['fields-pane']}>
							<div className={styles['fields-header']}>
								<div className={styles['header-title']}>
									<div className={styles['hint-title']}>
										Select fields whose data will populate the column.
									</div>
									<div className={styles['hint-sub']}>
										Only one field per Report Type can be selected.
									</div>
								</div>

								<div className={styles.actions}>
									<div className={styles['search-field']}>
										<SearchInput
											label="Search Fields"
											name="searchFields"
											onChange={setSearch}
											placeholder="Search"
											value={search}
										/>
									</div>
									{
										enableBulkActions &&
										<div className={styles['action-buttons']}>
											<div
												className={`${styles['action-button']} ${isMarkOthersAsDoNotUseDisabled ? styles['action-button--disabled'] : ''}`}
												onClick={isMarkOthersAsDoNotUseDisabled ? undefined : handleMarkOthersAsDoNotUse}
											>
												Mark others as Do Not Use
												<Tooltip message={TOOLTIP_MESSAGE}>
													<span className={`icon-info ${styles['info-icon']}`} />
												</Tooltip>
											</div>
											<div
												className={`${styles['action-button']} ${isClearAllDisabled ? styles['action-button--disabled'] : ''}`}
												onClick={isClearAllDisabled ? undefined : handleClearAll}
											>
												Clear All
											</div>
										</div>
									}
								</div>
							</div>

							<div className={styles['picker-area']}>
								<div className={styles['picker-inner']}>
									<ReportTypeCards
										exclusionModeByLatestId={exclusionModeByLatestId}
										hasAssociatedOldRevisionByLatestId={hasAssociatedOldRevisionByLatestId}
										onMarkDoNotUse={handleMarkDoNotUse}
										onSelectField={handleSelectField}
										openRevisionsFor={openRevisionsFor}
										outdatedLatestIds={outdatedLatestIds}
										reportTypes={latestReportTypes}
										searchTerm={search}
										selectedFields={selectedFields}
									/>
								</div>
							</div>
						</div>

						<div className={styles['right-pane']}>
							<FieldsPane
								doNotUseReportTypes={doNotUseReportTypes}
								onDisableSelected={handleDisable}
								outdatedFields={outdatedFields}
								previouslySelectedReportTypes={previouslySelectedReportTypes}
								selectedColumns={selectedColumns}
								unassociatedReportTypes={unAssociatedReportTypes}
							/>
						</div>
					</div>
				</CustomModal.Body>

				<CustomModal.Footer>
					<Button
						label="Close"
						onClick={closeModal}
						style="secondary"
					/>
					<Button
						disabled={isSaveDisabled}
						label="Save"
						onClick={onSave}
						style="primary"
					/>
				</CustomModal.Footer>
			</CustomModal >
			{showRevisions && revisionsLatestRtId !== null && (
				<ReportTypeRevisionsModal
					column={column!}
					companyFieldReportTypes={companyFieldReportTypes}
					latestReportTypeId={revisionsLatestRtId}
					onApply={applyRevisionsPatch}
					onClose={closeRevisions}
					show={showRevisions}
				/>
			)}
		</>
	);
};

export default React.memo(AddFieldsToColumnsModal);
